/**
 * Reauth Service
 * Manages the reauth modal and queues failed requests for retry
 */

import type { AxiosRequestConfig } from 'axios'

type RequestRetry = {
  config: AxiosRequestConfig
  resolve: (value: any) => void
  reject: (reason: any) => void
}

class ReauthService {
  private isReauthModalOpen = false
  private pendingRequests: RequestRetry[] = []
  private modalOpenListener: ((isOpen: boolean) => void) | null = null

  /**
   * Set the listener for modal open/close events
   */
  setModalOpenListener(listener: (isOpen: boolean) => void) {
    this.modalOpenListener = listener
  }

  /**
   * Check if reauth modal is currently open
   */
  isModalOpen(): boolean {
    return this.isReauthModalOpen
  }

  /**
   * Show reauth modal and queue the failed request for retry
   */
  showReauthModal(config: AxiosRequestConfig): Promise<any> {
    return new Promise((resolve, reject) => {
      // Add request to queue
      this.pendingRequests.push({ config, resolve, reject })

      // Only open modal once (even if multiple requests fail)
      if (!this.isReauthModalOpen) {
        this.isReauthModalOpen = true
        if (this.modalOpenListener) {
          this.modalOpenListener(true)
        }
      }
    })
  }

  /**
   * Handle successful reauth - retry all pending requests
   */
  async handleReauthSuccess(token: string, apiClient: any) {
    // Update token in sessionStorage
    sessionStorage.setItem('codewords_admin_token', token)

    // Close modal
    this.isReauthModalOpen = false
    if (this.modalOpenListener) {
      this.modalOpenListener(false)
    }

    // Retry all pending requests with new token
    const requests = [...this.pendingRequests]
    this.pendingRequests = []

    for (const { config, resolve, reject } of requests) {
      try {
        // Update config with new token
        if (config.headers) {
          config.headers.Authorization = `Bearer ${token}`
        }

        // Retry the request
        const response = await apiClient.request(config)
        resolve(response)
      } catch (error) {
        reject(error)
      }
    }
  }

  /**
   * Handle reauth cancellation - reject all pending requests
   */
  handleReauthCancel() {
    this.isReauthModalOpen = false
    if (this.modalOpenListener) {
      this.modalOpenListener(false)
    }

    // Reject all pending requests
    const requests = [...this.pendingRequests]
    this.pendingRequests = []

    const cancelError = new Error('Authentication cancelled by user')
    for (const { reject } of requests) {
      reject(cancelError)
    }
  }

  /**
   * Clear all pending requests (e.g., on logout)
   */
  clear() {
    this.isReauthModalOpen = false
    this.pendingRequests = []
    if (this.modalOpenListener) {
      this.modalOpenListener(false)
    }
  }
}

export const reauthService = new ReauthService()
